import React from "react";
import {
  CANDIDATE_TIMESHEET_DECLINED,
  CANDIDATE_TIMESHEET_PENDING_APPROVAL,
} from "../../../constants/app";
import { TTimesheetVacancyData } from "../../../store/timesheet/types";
import { extractSuburbFromAddress } from "../../../utils/stringUtils";
import CustomButton from "../../UI/Buttons/CustomButton";
import CancelCircle from "../../UI/Icons/CancelCircle";
import EditPencil from "../../UI/Icons/EditPencil";
import CentreImage from "../../Vacancy/CentreImage";

/**
 * Props for the ActiveTimeSheetListingCard component.
 *
 * @property _timesheet - The timesheet vacancy data to display, or null if not available.
 * @property formatJobTime - Function to format the job's start and end times into a string.
 * @property showViewTimesheetModal - Boolean indicating whether the view timesheet modal is visible.
 * @property setShowViewTimesheetModal - Function to set the visibility of the view timesheet modal.
 * @property setCurrentTimeSheet - (Optional) Function to set the currently selected timesheet data.
 * @property setShowReviewModal - Function to set the visibility of the review modal.
 * @property setCurrentReviewTimeSheet - (Optional) Function to set the currently selected timesheet data for review.
 */
type ActiveTimeSheetListingCardProps = {
  _timesheet: TTimesheetVacancyData | null;
  formatJobTime: (
    job_start_time: string | null,
    job_end_time: string | null
  ) => string;
  showViewTimesheetModal: boolean;
  setShowViewTimesheetModal: (value: boolean) => void;
  setCurrentTimeSheet?: (timeSheetData: TTimesheetVacancyData) => void;
  setShowReviewModal: (value: boolean) => void;
  setCurrentReviewTimeSheet?: (timeSheetData: TTimesheetVacancyData) => void;
  setShowEditModal?: (value: boolean) => void;
  setCurrentEditTimeSheet?: (timeSheetData: TTimesheetVacancyData) => void;
};

/**
 * ActiveTimeSheetListingCard component displays a card for an active timesheet listing.
 * It shows the centre's details, job timeframe, status, and provides buttons to view, review, or edit the timesheet.
 *
 * @param {ActiveTimeSheetListingCardProps} props - The properties for the component.
 * @returns {JSX.Element} The rendered component.
 */
const ActiveTimeSheetListingCard: React.FC<ActiveTimeSheetListingCardProps> = ({
  _timesheet,
  formatJobTime,
  showViewTimesheetModal,
  setShowViewTimesheetModal,
  setCurrentTimeSheet,
  setShowReviewModal,
  setCurrentReviewTimeSheet,
  setShowEditModal,
  setCurrentEditTimeSheet
}) => {
  /**
   * Function to get the status of the timesheet based on its status ID.
   * It returns a string representation of the status.
   * @param status
   * @returns
   */
  const getStatusOfTimesheet = (status: number | undefined) => {
    if (status === CANDIDATE_TIMESHEET_DECLINED) {
      return "Declined";
    } else if (status === CANDIDATE_TIMESHEET_PENDING_APPROVAL) {
      return "Pending Approval";
    } else if (status === undefined) {
      return "";
    }

    return "";
  };

  /**
   * Handles the action of viewing a timesheet.
   * It sets the modal visibility to true and updates the current timesheet data.
   * @param timeSheetData
   */
  const handleViewTimeSheet = (timeSheetData: TTimesheetVacancyData) => {
    setShowViewTimesheetModal(true);
    setCurrentTimeSheet && setCurrentTimeSheet(timeSheetData);
  };

  /**
   * Handles the action of reviewing a timesheet.
   * It sets the review modal visibility to true and updates the current review timesheet data.
   */
  const handleReviewViewTimeSheet = () => {
    setShowReviewModal(true);
    setCurrentReviewTimeSheet &&
      setCurrentReviewTimeSheet(_timesheet as TTimesheetVacancyData);
  };

  /**
   * Handles the action of editing a timesheet.
   * It sets the edit modal visibility to true and updates the current timesheet data for editing.
   */
  const handleEditTimeSheet = () => {
    setShowEditModal && setShowEditModal(true);
    setCurrentEditTimeSheet && setCurrentEditTimeSheet(_timesheet as TTimesheetVacancyData);
  };



  return (
    
      <div className="border-bottom border-off-white p-15px d-flex gap-20 align-items-lg-center">
        <div className="w-50px h-50px  border-2 border-grey-dark radius-40px">
          <CentreImage
            imageUrl={_timesheet?.centre?.photo ?? null}
            width="50px"
            height="50px"
            isCandidateFilledAllForms={true}
          />
        </div>
        <div className="flex-grow-1 d-flex flex-column flex-lg-row gap-20 align-items-lg-center">
          <div className="flex-grow-1 d-flex flex-column gap-3 text-break">
            <div className="fs-16 fw-700 text-black">
              {_timesheet?.centre?.business_name},{" "}
              <span className="fw-400">{extractSuburbFromAddress(_timesheet?.centre?.address || "")}</span>
            </div>
            <div className="fs-13 fw-400 text-black">
              {_timesheet?.vacancy?.matching_position?.name ?? ""}
            </div>
          </div>
          <div className="min-w-250px w-205px text-break fs-13 fw-400 text-black">
            <span className="d-block d-lg-none fw-700">Job Timeframe</span>
            {/* { formatReadableTimeSlot(
              format(new Date(_timesheet?.job_start_time ?? ""), "yyyy-MM-dd"), 
              _timesheet?.timeframe?.start_time || "", 
              _timesheet?.timeframe?.end_time || "")
            } <br /> */}

            {
              _timesheet?.timesheet_date_time || "-"
            }
            
            <div className="fw-700 fs-13 mt-2 d-flex gap-10 align-items-center">{_timesheet?.did_not_work ? <><CancelCircle /> I did not work on this day</> : ""}</div>
          </div>
          <div className="d-flex gap-10 align-items-center justify-content-lg-center min-w-150px">
            <div className="d-block d-lg-none fs-13 fw-700 text-black">
              Status:
            </div>
            <div className="border border-alert radius-8px px-8px py-4px fs-13 fw-400 text-alert">
              {getStatusOfTimesheet(_timesheet?.status_id)}              
            </div>
          </div>
          <div className="min-w-130px">
            {_timesheet?.status_id === CANDIDATE_TIMESHEET_PENDING_APPROVAL && (
              <CustomButton
                variant={"primary"}
                type="button"
                className="btn-theme bg-teal text-black w-100"
                onClick={() => handleEditTimeSheet()}
              >
                <EditPencil />
                Edit
              </CustomButton>
            )}
            {_timesheet?.status_id === CANDIDATE_TIMESHEET_DECLINED && (
              <CustomButton
                variant={"primary"}
                type="button"
                className="btn-theme bg-teal text-black w-100"
                onClick={() => handleReviewViewTimeSheet()}
              >
                <EditPencil />
                Review
              </CustomButton>
            )}
          </div>
        </div>
      </div>
   
  );
};

export default ActiveTimeSheetListingCard;
